import {
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  ContactValue,
  CoordinateValue,
  JsonSchemaType,
  PhoneNumberEntry,
  Object as PortalObject,
  Position,
  StatusColor,
  StatusOption,
  StatusValue,
} from '@gmn-portal/backend';
import type {
  Column,
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Check,
  Eye,
  Filter,
  Fuel,
  GripVertical,
  Lightbulb,
  MoreHorizontal,
  PackageSearch,
  Plane,
  Plus,
  SlidersHorizontal,
  Stethoscope,
  TriangleAlert,
  X,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useCustomer,
  useCustomers,
  useDeleteObject,
  useObjectCategories,
  useObjectCategory,
  useObjects,
  useObjectsOverview,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import { ContactEmailLink } from '@/shared/components/metadata/contact-email-link';
import { ContactPhoneLink } from '@/shared/components/metadata/contact-phone-link';
import {
  SortableHeader,
  TableActiveFilterTags,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableClearAllButton,
  TableFiltersBar,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import { useCoordinateFormat } from '@/shared/hooks/use-coordinate-format';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { cn, formatStoredCoordinatePart } from '@/shared/utils';
import { type FieldPermission, getFieldPermission } from '@/shared/utils/metadata';
import { isHelicopterCategoryName, isOffshoreCategoryName } from '@/shared/utils/object-categories';
import {
  getObjectCategoryIcon,
  normalizeObjectCategoryIconKey,
} from '@/shared/utils/object-category-icons';
import {
  canCreateService,
  canDeleteService,
  HELI_PROVIDER_GROUP_KEY,
  isJuhDispatcherGroup,
  resolveUserGroup,
  resolveUserGroupDomain,
  resolveUserGroupKey,
} from '@/shared/utils/permissions';
import { getCurrentStatusResolution } from '@/shared/utils/status';
import { getSortParam } from '@/shared/utils/table-utils';
import { SystemShipsOverview } from './system-ships-overview';

interface ObjectsOverviewProps {
  filterForCurrentUserCustomer?: boolean;
  embedded?: boolean;
  hidePageTitle?: boolean;
  tableId?: string;
  forcedWhereAnd?: Record<string, unknown>[];
  forceHelicopterTableView?: boolean;
}

type BooleanFilterValue = 'any' | 'true' | 'false';
type NumberRangeFilterValue = { min?: string; max?: string };
type DateRangeFilterValue = { from?: string; to?: string };
type TranslateFn = ReturnType<typeof useIntl>['t'];
type MetadataFilterValue =
  | string
  | BooleanFilterValue
  | NumberRangeFilterValue
  | DateRangeFilterValue
  | string[];

type CategoryPreferenceState = {
  metadataFilters: Record<string, MetadataFilterValue>;
  relationFilters?: Record<string, string>;
  columnVisibility?: Record<string, boolean>;
  columnOrder?: string[];
  metadataColumnOrder?: string[];
  visibleMetadataColumns?: string[];
};

type MetadataField = {
  key: string;
  label: string;
  type: string;
  format?: string;
  permission: FieldPermission;
  enumOptions?: string[];
  statusOptions?: StatusOption[];
  icon?: string;
  filterFavorite?: boolean;
};

type RelationFilterField = {
  key: 'linkedWindpark' | 'linkedShips';
  label: string;
};

type StatusValueWithOptionId = StatusValue & {
  manualStatusOptionId?: string;
  defaultStatusOptionId?: string;
  ranges?: Array<
    StatusValue['ranges'] extends Array<infer TR>
      ? TR & { statusDuringOptionId?: string; fallbackAfterOptionId?: string }
      : never
  >;
};

type ColumnDescriptor = {
  id: string;
  label: string;
  type: 'base' | 'metadata';
  metadataKey?: string;
};

const SHIP_CATEGORY_NAMES = ['schiff'] as const;
const WINDPARK_CATEGORY_NAMES = ['windpark'] as const;
// Categories JUH dispatchers are allowed to create (see backend objects.utils.ts).
const DISPONENT_CREATE_CATEGORY_NAMES = ['insel', 'klinik', 'land', 'tankmöglichkeiten'] as const;
const RELATION_CELL_VISIBLE_NAMES = 2;

const buildCategorySearch = (
  categoryFilter: string | null,
  baseParams?: Record<string, string>
) => {
  const params = new URLSearchParams(baseParams);
  if (categoryFilter) {
    params.set('category', categoryFilter);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

const normalizeCategoryName = (name: string | null | undefined) => name?.trim().toLowerCase() ?? '';

const isShipCategoryName = (name: string | null | undefined) =>
  SHIP_CATEGORY_NAMES.includes(normalizeCategoryName(name) as (typeof SHIP_CATEGORY_NAMES)[number]);

const isWindparkCategoryName = (name: string | null | undefined) =>
  WINDPARK_CATEGORY_NAMES.includes(
    normalizeCategoryName(name) as (typeof WINDPARK_CATEGORY_NAMES)[number]
  );

const isDisponentCreateCategoryName = (name: string | null | undefined) =>
  DISPONENT_CREATE_CATEGORY_NAMES.includes(
    normalizeCategoryName(name) as (typeof DISPONENT_CREATE_CATEGORY_NAMES)[number]
  );

const normalizeRelationshipId = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'id' in value) {
    const id = (value as { id?: unknown }).id;
    return typeof id === 'string' ? id : null;
  }
  return null;
};

const normalizeRelationshipIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => normalizeRelationshipId(entry))
    .filter((id): id is string => Boolean(id));
};

const getTodayDate = () => DateTime.now().toFormat('yyyy-MM-dd');

const isObjectActiveToday = (object: PortalObject) => {
  const today = getTodayDate();
  return (object.activePeriods || []).some(
    (period) =>
      Boolean(period.startDate) &&
      Boolean(period.endDate) &&
      period.startDate <= today &&
      period.endDate >= today
  );
};

const formatRelationNames = (names: string[]) => {
  if (names.length === 0) {
    return {
      display: '-',
      full: '-',
    };
  }

  const visibleNames = names.slice(0, RELATION_CELL_VISIBLE_NAMES);
  const overflow = names.length - visibleNames.length;
  return {
    display: overflow > 0 ? `${visibleNames.join(', ')} +${overflow}` : visibleNames.join(', '),
    full: names.join(', '),
  };
};

type SortableMetadataColumnRowProps = {
  column: ColumnDescriptor;
  checked: boolean;
  moveLabel: string;
  onCheckedChange: (checked: boolean) => void;
};

function SortableMetadataColumnRow({
  column,
  checked,
  moveLabel,
  onCheckedChange,
}: SortableMetadataColumnRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const rowTestId =
    column.type === 'metadata' && column.metadataKey
      ? `metadata-column-row-${column.metadataKey}`
      : `objects-column-row-${column.id}`;
  const dragTestId =
    column.type === 'metadata' && column.metadataKey
      ? `metadata-column-drag-${column.metadataKey}`
      : `objects-column-drag-${column.id}`;
  const checkboxTestId =
    column.type === 'metadata' && column.metadataKey
      ? `metadata-column-${column.metadataKey}`
      : `objects-column-${column.id}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded px-2 py-1.5 text-sm text-foreground hover:bg-muted touch-none',
        isDragging && 'bg-muted/70'
      )}
      data-testid={rowTestId}
    >
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded border border-transparent text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-grab active:cursor-grabbing touch-none select-none"
        aria-label={moveLabel}
        data-testid={dragTestId}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} />
      </button>
      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
        <input
          type="checkbox"
          className="rounded border-border text-foreground focus:ring-ring"
          checked={checked}
          data-testid={checkboxTestId}
          onChange={(event) => onCheckedChange(event.target.checked)}
        />
        <span className="truncate" title={column.label}>
          {column.label}
        </span>
      </label>
    </div>
  );
}

function formatMetadataDate(value: unknown, includeTime?: boolean) {
  if (!value) return '-';
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : typeof value === 'string'
        ? DateTime.fromISO(value)
        : null;
  if (!dt || !dt.isValid) return String(value);
  return includeTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
}

function isFilterActive(value: MetadataFilterValue | undefined) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0 && value !== 'any';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    if ('min' in value) return Boolean(value.min) || Boolean(value.max);
    if ('from' in value) return Boolean(value.from) || Boolean(value.to);
  }
  return false;
}

function getActiveFilterCount(filters: Record<string, MetadataFilterValue>) {
  return Object.values(filters).reduce((count, v) => (isFilterActive(v) ? count + 1 : count), 0);
}

function resolveStatusOptionLabel(locale: string, opt: StatusOption) {
  return locale === 'de-DE' ? opt.labelDe : opt.labelEn || opt.labelDe;
}

function safeStatusValue(value: unknown): StatusValueWithOptionId | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as StatusValueWithOptionId;
  if (v.mode !== 'manual' && v.mode !== 'scheduled') return null;
  return v;
}

function resolveStatusOption(
  options: StatusOption[],
  color: StatusColor,
  optionId?: string
): StatusOption | null {
  if (optionId) {
    const byId = options.find((o) => o.id === optionId);
    if (byId) return byId;
  }
  return options.find((o) => o.color === color) ?? null;
}

function ensureStatusOptionsWithIds(options: StatusOption[]): Array<StatusOption & { id: string }> {
  return options.map((opt, index) => ({
    ...opt,
    id: opt.id?.trim() || `status-opt-${opt.color}-${index + 1}`,
  }));
}

function safeContactValue(value: unknown): Partial<ContactValue> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Partial<ContactValue>;
}

function safeCoordinateValue(value: unknown): CoordinateValue | null {
  if (!value || typeof value !== 'object') return null;
  return value as CoordinateValue;
}

function safePositionsValue(value: unknown): Position[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (row): row is Position =>
        row && typeof row === 'object' && typeof (row as any).id === 'string'
    )
    .map((row) => ({
      id: row.id,
      label: row.label ?? '',
      latitude: row.latitude ?? undefined,
      longitude: row.longitude ?? undefined,
      status: row.status,
    }));
}

function safePhoneNumbersValue(value: unknown): PhoneNumberEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is PhoneNumberEntry =>
      row && typeof row === 'object' && typeof (row as PhoneNumberEntry).id === 'string'
  );
}

const statusIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  fuel: Fuel,
  helideck: Plane,
  warning: TriangleAlert,
  check: Check,
  lightning: Lightbulb,
  medical: Stethoscope,
};

const statusDotClasses: Record<StatusColor, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const statusIconTextClasses: Record<StatusColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
};

const pointerFirstCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  return pointerHits.length > 0 ? pointerHits : closestCenter(args);
};

function StatusMarker({ color, icon }: { color: StatusColor; icon?: string }) {
  const IconComponent = icon ? statusIconMap[icon] : null;
  if (IconComponent) {
    return (
      <IconComponent
        className={cn('size-4', statusIconTextClasses[color])}
        data-testid={`status-indicator-icon-${color}`}
      />
    );
  }
  return (
    <span
      className={cn('h-2.5 w-2.5 rounded-full', statusDotClasses[color])}
      data-testid={`status-indicator-dot-${color}`}
    />
  );
}

function renderMetadataCell({
  field,
  value,
  t,
  locale,
  coordinateFormat,
  yesLabel,
  noLabel,
}: {
  field: MetadataField;
  value: unknown;
  t: TranslateFn;
  locale: string;
  coordinateFormat: ReturnType<typeof useCoordinateFormat>;
  yesLabel: string;
  noLabel: string;
}) {
  if (field.type === 'boolean') {
    const resolvedValue = value === undefined || value === null ? false : value;
    return (
      <div className="pt-0.5">
        <Badge variant="outline-solid">
          {resolvedValue === true ? yesLabel : resolvedValue === false ? noLabel : '-'}
        </Badge>
      </div>
    );
  }

  if (field.type === 'status') {
    const statusValue = safeStatusValue(value);
    const currentResolution = getCurrentStatusResolution(statusValue);
    const currentColor = currentResolution.color;
    const opts = ensureStatusOptionsWithIds(field.statusOptions ?? []);
    const currentOpt = resolveStatusOption(opts, currentColor, currentResolution.optionId);
    const label = currentOpt ? resolveStatusOptionLabel(locale, currentOpt) : currentColor;

    return (
      <span className="inline-flex items-center gap-2">
        <StatusMarker color={currentColor} icon={field.icon} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </span>
    );
  }

  if (value === undefined || value === null) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (field.type === 'number') {
    const v =
      typeof value === 'number'
        ? String(value)
        : typeof value === 'string' && value.trim() !== ''
          ? value
          : '-';
    return <span className="text-muted-foreground">{v}</span>;
  }

  if (field.type === 'date') {
    return (
      <span className="text-muted-foreground">
        {formatMetadataDate(value, field.format === 'date-time')}
      </span>
    );
  }

  if (field.type === 'contact') {
    const contact = safeContactValue(value);
    if (!contact) {
      return <span className="text-muted-foreground">-</span>;
    }

    const summary = contact.name || contact.phone1 || contact.email || '-';
    const secondary =
      contact.name && (contact.phone1 || contact.email)
        ? contact.phone1 || contact.email
        : undefined;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="min-w-0 block bg-transparent p-0 text-left outline-none">
            <div className="text-muted-foreground truncate max-w-[260px]">{summary}</div>
            {secondary && (
              <div className="text-xs text-muted-foreground/80 truncate max-w-[260px]">
                {secondary}
              </div>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[360px]">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <div className="text-muted-foreground">{t('metadata.contact.name')}</div>
              <div className="font-medium">{contact.name || '-'}</div>
              <div className="text-muted-foreground">{t('metadata.contact.email')}</div>
              <div className="font-medium">
                <ContactEmailLink value={contact.email} />
              </div>
              <div className="text-muted-foreground">{t('metadata.contact.phone1')}</div>
              <div className="font-medium">
                <ContactPhoneLink value={contact.phone1} />
              </div>
              <div className="text-muted-foreground">{t('metadata.contact.phone2')}</div>
              <div className="font-medium">
                <ContactPhoneLink value={contact.phone2} />
              </div>
            </div>

            {contact.fallbackContact && (
              <div className="border-t border-border pt-2">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('metadata.contact.fallbackContact')}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <div className="text-muted-foreground">{t('metadata.contact.name')}</div>
                  <div className="font-medium">{contact.fallbackContact.name || '-'}</div>
                  <div className="text-muted-foreground">{t('metadata.contact.email')}</div>
                  <div className="font-medium">
                    <ContactEmailLink value={contact.fallbackContact.email} />
                  </div>
                  <div className="text-muted-foreground">{t('metadata.contact.phone1')}</div>
                  <div className="font-medium">
                    <ContactPhoneLink value={contact.fallbackContact.phone1} />
                  </div>
                  <div className="text-muted-foreground">{t('metadata.contact.phone2')}</div>
                  <div className="font-medium">
                    <ContactPhoneLink value={contact.fallbackContact.phone2} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (field.type === 'coordinate') {
    const coordinate = safeCoordinateValue(value);
    if (!coordinate) {
      return <span className="text-muted-foreground">-</span>;
    }

    const latitude = formatStoredCoordinatePart(coordinate.latitude, 'latitude', coordinateFormat);
    const longitude = formatStoredCoordinatePart(
      coordinate.longitude,
      'longitude',
      coordinateFormat
    );
    const summary = [latitude || '-', longitude || '-'].join(' / ');

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="min-w-0 block bg-transparent p-0 text-left outline-none">
            <div className="truncate max-w-[260px] text-muted-foreground">{summary}</div>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[320px]">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <div className="text-muted-foreground">{t('metadata.positions.latitude')}</div>
            <div className="font-medium">{latitude || '-'}</div>
            <div className="text-muted-foreground">{t('metadata.positions.longitude')}</div>
            <div className="font-medium">{longitude || '-'}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (field.type === 'positions') {
    const positions = safePositionsValue(value);
    if (positions.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    const firstLabel = positions[0]?.label?.trim() || t('metadata.positions.label');
    const extraCount = positions.length - 1;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="min-w-0 block bg-transparent p-0 text-left outline-none">
            <div className="text-muted-foreground truncate max-w-[260px]">
              {firstLabel}
              {extraCount > 0 ? ` +${extraCount}` : ''}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[420px]">
          <div className="space-y-2">
            <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 text-xs font-medium text-muted-foreground">
              <div>{t('metadata.positions.label')}</div>
              <div>{t('metadata.positions.latitude')}</div>
              <div>{t('metadata.positions.longitude')}</div>
            </div>
            <div className="space-y-2">
              {positions.slice(0, 6).map((pos) => (
                <div
                  key={pos.id}
                  className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 rounded-md border border-border bg-muted/20 px-2 py-1 text-sm"
                >
                  <div className="font-medium truncate">{pos.label || '-'}</div>
                  <div className="truncate">
                    {formatStoredCoordinatePart(pos.latitude, 'latitude', coordinateFormat) || '-'}
                  </div>
                  <div className="truncate">
                    {formatStoredCoordinatePart(pos.longitude, 'longitude', coordinateFormat) ||
                      '-'}
                  </div>
                </div>
              ))}
              {positions.length > 6 && (
                <div className="text-xs text-muted-foreground">… +{positions.length - 6}</div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (field.type === 'phoneNumbers') {
    const phoneNumbers = safePhoneNumbersValue(value);
    if (phoneNumbers.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    const firstEntry = phoneNumbers[0];
    const firstLabel =
      firstEntry?.name?.trim() || firstEntry?.phone || t('metadata.phoneNumbers.name');
    const extraCount = phoneNumbers.length - 1;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="min-w-0 block bg-transparent p-0 text-left outline-none">
            <div className="text-muted-foreground truncate max-w-[260px]">
              {firstLabel}
              {extraCount > 0 ? ` +${extraCount}` : ''}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[360px]">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs font-medium text-muted-foreground">
              <div>{t('metadata.phoneNumbers.name')}</div>
              <div>{t('metadata.phoneNumbers.phone')}</div>
            </div>
            <div className="space-y-2">
              {phoneNumbers.slice(0, 6).map((entry) => (
                <div
                  key={entry.id}
                  className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted/20 px-2 py-1 text-sm"
                >
                  <div className="font-medium truncate">{entry.name || '-'}</div>
                  <div className="truncate">
                    <ContactPhoneLink value={entry.phone} />
                  </div>
                </div>
              ))}
              {phoneNumbers.length > 6 && (
                <div className="text-xs text-muted-foreground">… +{phoneNumbers.length - 6}</div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (typeof value === 'object') {
    let json = '';
    try {
      json = JSON.stringify(value);
    } catch {
      json = '[...]';
    }

    return (
      <span className="text-muted-foreground truncate max-w-[260px] block" title={json}>
        {json}
      </span>
    );
  }

  return (
    <span className="text-muted-foreground truncate max-w-[260px] block" title={String(value)}>
      {String(value)}
    </span>
  );
}

const defaultStatusOptions: StatusOption[] = [
  { id: 'status-opt-green', color: 'green', labelDe: 'Green', labelEn: 'Green' },
  { id: 'status-opt-yellow', color: 'yellow', labelDe: 'Yellow', labelEn: 'Yellow' },
  { id: 'status-opt-red', color: 'red', labelDe: 'Red', labelEn: 'Red' },
];

function formatFilterValueForChip(
  value: MetadataFilterValue,
  labels: { yes: string; no: string },
  field?: MetadataField,
  locale?: string
) {
  if (field?.type === 'status') {
    const options = ensureStatusOptionsWithIds(
      field.statusOptions?.length ? field.statusOptions : defaultStatusOptions
    );
    const labelFor = (rawStatusValue: string) => {
      const byId = options.find((o) => o.id === rawStatusValue);
      if (byId) return resolveStatusOptionLabel(locale || 'de-DE', byId);

      const byColor = options.find((o) => o.color === rawStatusValue);
      if (byColor) return resolveStatusOptionLabel(locale || 'de-DE', byColor);

      return rawStatusValue;
    };
    if (Array.isArray(value)) return value.map((v) => labelFor(v)).join(', ');
    if (typeof value === 'string') return labelFor(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    if ('min' in value) return `${value.min || ''} – ${value.max || ''}`.trim();
    if ('from' in value) return `${value.from || ''} – ${value.to || ''}`.trim();
  }
  if (value === 'true') return labels.yes;
  if (value === 'false') return labels.no;
  if (value === 'any') return '';
  return String(value);
}

function isFilterableField(field: MetadataField) {
  if (field.enumOptions?.length || field.type === 'status') return true;
  return (
    field.type === 'string' ||
    field.type === 'number' ||
    field.type === 'boolean' ||
    field.type === 'date'
  );
}

function MetadataFilterControl({
  field,
  value,
  onChange,
  t,
  locale,
  testIdPrefix,
}: {
  field: MetadataField;
  value: MetadataFilterValue | undefined;
  onChange: (value: MetadataFilterValue) => void;
  t: TranslateFn;
  locale: string;
  testIdPrefix: string;
}) {
  if (field.enumOptions?.length) {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <ToggleGroup
        type="multiple"
        value={selected}
        onValueChange={(vals) => onChange(vals)}
        className="flex flex-wrap justify-start gap-1.5"
      >
        {field.enumOptions.map((opt) => (
          <ToggleGroupItem
            key={opt}
            value={opt}
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-opt-${opt}`}
            className="h-7 px-2 text-xs"
          >
            {opt}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    );
  }

  if (field.type === 'status') {
    const statusOptions = ensureStatusOptionsWithIds(
      field.statusOptions?.length ? field.statusOptions : defaultStatusOptions
    );
    const selectedRaw = Array.isArray(value)
      ? (value as string[])
      : typeof value === 'string'
        ? [value]
        : [];
    const selected = Array.from(
      new Set(
        selectedRaw.map((selectedValue) => {
          const byId = statusOptions.find((opt) => opt.id === selectedValue);
          if (byId) return byId.id;

          const byColor = statusOptions.find((opt) => opt.color === selectedValue);
          return byColor?.id || selectedValue;
        })
      )
    );

    return (
      <ToggleGroup
        type="multiple"
        value={selected}
        onValueChange={(vals) => onChange(vals)}
        className="flex flex-wrap justify-start gap-1.5"
      >
        {statusOptions.map((opt) => (
          <ToggleGroupItem
            key={opt.id}
            value={opt.id}
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-opt-${opt.id}`}
            className="h-7 px-2 text-xs"
          >
            <span className="inline-flex items-center gap-2">
              <StatusMarker color={opt.color} icon={field.icon} />
              <span>{resolveStatusOptionLabel(locale, opt)}</span>
            </span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    );
  }

  if (field.type === 'string') {
    return (
      <Input
        data-testid={testIdPrefix}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('objects.containsPlaceholder')}
        className="h-8"
      />
    );
  }

  if (field.type === 'number') {
    const current =
      typeof value === 'object' && value && ('min' in value || 'max' in value)
        ? (value as NumberRangeFilterValue)
        : {};

    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          placeholder={t('common.from')}
          data-testid={`${testIdPrefix}-min`}
          value={current.min || ''}
          onChange={(e) => onChange({ ...current, min: e.target.value })}
          className="w-28 h-8"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="number"
          inputMode="numeric"
          placeholder={t('common.to')}
          data-testid={`${testIdPrefix}-max`}
          value={current.max || ''}
          onChange={(e) => onChange({ ...current, max: e.target.value })}
          className="w-28 h-8"
        />
      </div>
    );
  }

  if (field.type === 'date') {
    const current =
      typeof value === 'object' && value && ('from' in value || 'to' in value)
        ? (value as DateRangeFilterValue)
        : {};
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs w-10 text-muted-foreground">{t('common.from')}</span>
          <Input
            type="date"
            data-testid={`${testIdPrefix}-from`}
            value={current.from || ''}
            onChange={(e) => onChange({ ...current, from: e.target.value })}
            className="h-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs w-10 text-muted-foreground">{t('common.to')}</span>
          <Input
            type="date"
            data-testid={`${testIdPrefix}-to`}
            value={current.to || ''}
            onChange={(e) => onChange({ ...current, to: e.target.value })}
            className="h-8"
          />
        </div>
      </div>
    );
  }

  if (field.type === 'boolean') {
    const selected = typeof value === 'string' ? (value as BooleanFilterValue) : 'any';
    return (
      <Select value={selected} onValueChange={(v) => onChange(v as BooleanFilterValue)}>
        <SelectTrigger data-testid={`${testIdPrefix}-bool`} className="h-8">
          <SelectValue placeholder={t('common.all')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">{t('common.all')}</SelectItem>
          <SelectItem value="true">{t('common.yes')}</SelectItem>
          <SelectItem value="false">{t('common.no')}</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return null;
}

const ActionCell = ({
  object,
  onDelete,
  onViewDetail,
  categoryFilter,
  filterForCurrentUserCustomer = false,
}: {
  object: PortalObject;
  onDelete: (object: PortalObject) => void;
  onViewDetail: (object: PortalObject) => void;
  categoryFilter: string | null;
  filterForCurrentUserCustomer?: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const group = resolveUserGroup(currentUser);
  const canCopy = group ? canCreateService(group, 'objects') : false;
  const canDelete = group ? canDeleteService(group, 'objects') : false;

  // Determine the appropriate route based on context
  const editRouteBase = filterForCurrentUserCustomer
    ? `/c/objects/${object.id}`
    : `/gmn/objects/${object.id}`;
  const editRoute = `${editRouteBase}${buildCategorySearch(categoryFilter)}`;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Container only stops propagation, not interactive
    <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
        <button
          type="button"
          onClick={() => onViewDetail(object)}
          className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
          title={t('common.view')}
        >
          <Eye size={16} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => navigate(editRoute)}>
              {filterForCurrentUserCustomer ? t('common.view') : t('common.edit')}
            </DropdownMenuItem>
            {!filterForCurrentUserCustomer && !normalizeRelationshipId(object.systemShip) && (
              <DropdownMenuItem
                onClick={() => {
                  const search = buildCategorySearch(categoryFilter, { copy: object.id });
                  navigate(`/gmn/objects/new${search}`);
                }}
                disabled={!canCopy}
                title={!canCopy ? t('objects.onlyGmnAdminCanCopy') : undefined}
              >
                {t('objects.copyObject')}
              </DropdownMenuItem>
            )}
            {canDelete && !filterForCurrentUserCustomer && (
              <DropdownMenuItem onClick={() => onDelete(object)}>
                {t('common.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const CustomerCell = ({
  assignedCustomer,
}: {
  assignedCustomer?: { id: string; name?: string } | string | null;
}) => {
  const customerId = assignedCustomer
    ? typeof assignedCustomer === 'object'
      ? assignedCustomer.id
      : assignedCustomer
    : '';

  const { data: customer, isLoading } = useCustomer(customerId);

  if (!assignedCustomer) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof assignedCustomer === 'object' && assignedCustomer.name) {
    return (
      <span className="text-muted-foreground truncate" title={assignedCustomer.name}>
        {assignedCustomer.name}
      </span>
    );
  }

  if (isLoading) {
    return <span className="text-muted-foreground">...</span>;
  }

  if (!customer) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="text-muted-foreground truncate" title={customer.name}>
      {customer.name}
    </span>
  );
};

const CategoryCell = ({
  category,
}: {
  category?: { id: string; name?: string } | string | null;
}) => {
  const categoryId = category ? (typeof category === 'object' ? category.id : category) : '';

  const { data: categoryData, isLoading } = useObjectCategory(categoryId, {
    disabled: !categoryId,
  });

  if (!category) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof category === 'object' && category.name) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
        {category.name}
      </span>
    );
  }

  if (isLoading) {
    return <span className="text-muted-foreground">...</span>;
  }

  if (!categoryData) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
      {categoryData.name}
    </span>
  );
};

export function ObjectsOverview({
  filterForCurrentUserCustomer = false,
  embedded = false,
  hidePageTitle = false,
  tableId,
  forcedWhereAnd = [],
  forceHelicopterTableView = false,
}: ObjectsOverviewProps) {
  const { t, locale } = useIntl();
  const coordinateFormat = useCoordinateFormat();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const group = resolveUserGroup(currentUser);
  const isCustomerDomain = resolveUserGroupDomain(currentUser) === 'customer';
  const groupKey = resolveUserGroupKey(currentUser);
  const isHeliProvider = groupKey === HELI_PROVIDER_GROUP_KEY;
  const canCreateObjects = group ? canCreateService(group, 'objects') : false;
  const yesLabel = t('common.yes');
  const noLabel = t('common.no');

  const tablePreferences = useTablePreferences({
    tableId: tableId ?? (filterForCurrentUserCustomer ? 'my-objects-overview' : 'objects-overview'),
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
      selectedFilters: {
        categoryPreferences: {},
      },
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const initialCategoryPreferences =
    (preferences.selectedFilters?.categoryPreferences as
      | Record<string, CategoryPreferenceState>
      | undefined) ?? {};
  const [categoryPreferences, setCategoryPreferences] = useState<
    Record<string, CategoryPreferenceState>
  >(initialCategoryPreferences);

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
      selectedFilters: {
        categoryPreferences,
      },
    });
  }, [
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    searchTerm,
    categoryPreferences,
    tablePreferences,
  ]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Get category filter from URL
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const previousCategoryFilterRef = useRef<string | null>(categoryFilter);
  const { data: filteredCategory, isLoading: isCategoryLoading } = useObjectCategory(
    categoryFilter || '',
    {
      disabled: !categoryFilter,
    }
  );
  const { data: shipCategoryOptions, isLoading: isShipCategoryOptionsLoading } =
    useObjectCategories({
      limit: 100,
      disabled: Boolean(categoryFilter),
    });
  const shipCategoryIds = useMemo(
    () =>
      (shipCategoryOptions?.docs ?? [])
        .filter((category) => isShipCategoryName(category.name))
        .map((category) => String(category.id)),
    [shipCategoryOptions?.docs]
  );

  useEffect(() => {
    if (previousCategoryFilterRef.current === categoryFilter) {
      return;
    }

    previousCategoryFilterRef.current = categoryFilter;
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [categoryFilter]);
  const normalizedFilteredCategoryName = normalizeCategoryName(filteredCategory?.name);
  const isHelicopterCategoryFilter = isHelicopterCategoryName(normalizedFilteredCategoryName);
  const isOffshoreCategoryFilter = isOffshoreCategoryName(normalizedFilteredCategoryName);
  // JUH dispatchers can only create when the list is filtered to an
  // island/clinic/land/fuel-station category (which is then preselected on the create page).
  const canCreateObjectsForCategory =
    canCreateObjects ||
    (isJuhDispatcherGroup(currentUser) &&
      isDisponentCreateCategoryName(normalizedFilteredCategoryName));

  const metadataSchema =
    (filteredCategory?.metadataSchema as JsonSchemaType | undefined) ?? undefined;
  const metadataFields = useMemo<MetadataField[]>(() => {
    const properties = metadataSchema?.properties;
    if (!properties) return [];
    const order = metadataSchema?.propertyOrder?.length
      ? metadataSchema.propertyOrder
      : Object.keys(properties);

    return order
      .map((key) => {
        const def = properties[key] as any;
        const label =
          locale === 'en-US' &&
          typeof def.description_en === 'string' &&
          def.description_en.trim() !== ''
            ? def.description_en
            : def.description || key;
        const permission = getFieldPermission(def, groupKey ?? null);

        return {
          key,
          label,
          type: def.type,
          format: def.format,
          permission,
          enumOptions: Array.isArray(def.enum) ? (def.enum as string[]) : undefined,
          statusOptions: Array.isArray(def.statusOptions)
            ? (def.statusOptions as StatusOption[])
            : undefined,
          icon: typeof def.icon === 'string' ? def.icon : undefined,
          filterFavorite: def.filterFavorite === true,
        } satisfies MetadataField;
      })
      .filter(
        (field) =>
          field.type !== 'section' && field.type !== 'relation-display' && field.type !== 'photos'
      )
      .filter((field) => field.permission.read);
  }, [metadataSchema, locale, groupKey]);

  const filterableMetadataFields = metadataFields.filter(isFilterableField);
  const favoriteFilterFields = filterableMetadataFields.filter((f) => f.filterFavorite);
  const metadataFieldByKey = useMemo(
    () => new Map(metadataFields.map((f) => [f.key, f])),
    [metadataFields]
  );
  const relationFilterFields = useMemo<RelationFilterField[]>(() => {
    const categoryName = filteredCategory?.name || null;
    if (isShipCategoryName(categoryName)) {
      return [{ key: 'linkedWindpark', label: t('objects.linkedWindpark') }];
    }
    if (isWindparkCategoryName(categoryName)) {
      return [{ key: 'linkedShips', label: t('objects.linkedShips') }];
    }
    return [];
  }, [filteredCategory?.name, t]);
  const metadataFieldKeys = useMemo(() => metadataFields.map((f) => f.key), [metadataFields]);
  const metadataColumnIds = useMemo(
    () => metadataFieldKeys.map((key) => `meta:${key}`),
    [metadataFieldKeys]
  );

  const baseColumnDescriptors = useMemo<ColumnDescriptor[]>(() => {
    const categoryName = filteredCategory?.name || null;
    const columns: ColumnDescriptor[] = [
      { id: 'description', label: t('common.description'), type: 'base' },
    ];

    if (
      !categoryFilter ||
      isHelicopterCategoryName(categoryName) ||
      isOffshoreCategoryName(categoryName)
    ) {
      columns.push({
        id: 'assignedHelicopterServiceProvider',
        label: t('customers.assignedHelicopterServiceProvider'),
        type: 'base',
      });
    }

    if (!categoryFilter || isHelicopterCategoryName(categoryName)) {
      columns.push({
        id: 'linkedOffshoreObjects',
        label: t('objects.linkedOffshoreObjects'),
        type: 'base',
      });
    }

    if (!categoryFilter || isOffshoreCategoryName(categoryName)) {
      columns.push({
        id: 'linkedHelicopters',
        label: t('objects.linkedHelicopters'),
        type: 'base',
      });
    }

    if (isShipCategoryName(categoryName)) {
      columns.push({ id: 'linkedWindpark', label: t('objects.linkedWindpark'), type: 'base' });
    }

    if (isWindparkCategoryName(categoryName)) {
      columns.push({ id: 'linkedShips', label: t('objects.linkedShips'), type: 'base' });
    }

    if (!filterForCurrentUserCustomer) {
      columns.push({ id: 'category', label: t('objectCategories.category'), type: 'base' });
      columns.push({
        id: 'assignedCustomer',
        label: t('customers.assignedCustomer'),
        type: 'base',
      });
    }
    return columns;
  }, [categoryFilter, filteredCategory?.name, filterForCurrentUserCustomer, t]);
  const showAssignedHelicopterServiceProviderColumn = useMemo(() => {
    const categoryName = filteredCategory?.name || null;
    return (
      !categoryFilter ||
      isHelicopterCategoryName(categoryName) ||
      isOffshoreCategoryName(categoryName)
    );
  }, [categoryFilter, filteredCategory?.name]);
  const showLinkedOffshoreObjectsColumn = useMemo(() => {
    const categoryName = filteredCategory?.name || null;
    return !categoryFilter || isHelicopterCategoryName(categoryName);
  }, [categoryFilter, filteredCategory?.name]);
  const showLinkedHelicoptersColumn = useMemo(() => {
    const categoryName = filteredCategory?.name || null;
    return !categoryFilter || isOffshoreCategoryName(categoryName);
  }, [categoryFilter, filteredCategory?.name]);
  const showLinkedWindparkColumn = useMemo(
    () => isShipCategoryName(filteredCategory?.name),
    [filteredCategory?.name]
  );
  const showLinkedShipsColumn = useMemo(
    () => isWindparkCategoryName(filteredCategory?.name),
    [filteredCategory?.name]
  );

  const metadataColumnDescriptors = useMemo<ColumnDescriptor[]>(
    () =>
      metadataFields.map((field) => ({
        id: `meta:${field.key}`,
        label: field.label,
        type: 'metadata',
        metadataKey: field.key,
      })),
    [metadataFields]
  );

  const columnDescriptors = useMemo(
    () => [...baseColumnDescriptors, ...metadataColumnDescriptors],
    [baseColumnDescriptors, metadataColumnDescriptors]
  );
  const columnDescriptorById = useMemo(
    () => new Map(columnDescriptors.map((col) => [col.id, col])),
    [columnDescriptors]
  );
  const baseColumnIds = useMemo(
    () => baseColumnDescriptors.map((col) => col.id),
    [baseColumnDescriptors]
  );

  const defaultVisibleMetadataColumns = useMemo(() => {
    const simple = metadataFields.filter(
      (f) =>
        f.enumOptions?.length ||
        f.type === 'string' ||
        f.type === 'number' ||
        f.type === 'boolean' ||
        f.type === 'date'
    );
    return simple.slice(0, 5).map((f) => f.key);
  }, [metadataFields]);

  const defaultColumnOrderWithoutName = useMemo(
    () => [...baseColumnIds, ...metadataColumnIds],
    [baseColumnIds, metadataColumnIds]
  );

  const defaultColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    baseColumnIds.forEach((id) => {
      if (id === 'category') {
        visibility[id] = !categoryFilter;
        return;
      }

      if (id === 'assignedHelicopterServiceProvider') {
        visibility[id] =
          Boolean(categoryFilter) && (isHelicopterCategoryFilter || isOffshoreCategoryFilter);
        return;
      }

      if (id === 'linkedOffshoreObjects') {
        visibility[id] = Boolean(categoryFilter) && isHelicopterCategoryFilter;
        return;
      }

      if (id === 'linkedHelicopters') {
        visibility[id] = Boolean(categoryFilter) && isOffshoreCategoryFilter;
        return;
      }

      if (id === 'linkedWindpark') {
        visibility[id] = Boolean(categoryFilter) && isShipCategoryName(filteredCategory?.name);
        return;
      }

      if (id === 'linkedShips') {
        visibility[id] = Boolean(categoryFilter) && isWindparkCategoryName(filteredCategory?.name);
        return;
      }

      visibility[id] = true;
    });
    metadataFieldKeys.forEach((key) => {
      visibility[`meta:${key}`] = defaultVisibleMetadataColumns.includes(key);
    });
    visibility.actions = true;
    visibility.name = true;
    return visibility;
  }, [
    baseColumnIds,
    categoryFilter,
    defaultVisibleMetadataColumns,
    filteredCategory?.name,
    isHelicopterCategoryFilter,
    isOffshoreCategoryFilter,
    metadataFieldKeys,
  ]);
  const defaultGlobalColumnVisibility = useMemo(
    () => ({
      assignedHelicopterServiceProvider: forceHelicopterTableView,
      linkedOffshoreObjects: forceHelicopterTableView,
      linkedHelicopters: false,
      linkedWindpark: false,
      linkedShips: false,
      category: !forceHelicopterTableView,
    }),
    [forceHelicopterTableView]
  );

  const currentCategoryPref = categoryFilter ? categoryPreferences[categoryFilter] : undefined;

  useEffect(() => {
    if (!categoryFilter || isCategoryLoading) return;
    const validMetadataKeys = new Set(metadataFieldKeys);
    const validColumnIds = new Set(defaultColumnOrderWithoutName);

    const existing = currentCategoryPref;
    const metadataFilters: Record<string, MetadataFilterValue> = {};
    for (const [k, v] of Object.entries(existing?.metadataFilters || {})) {
      if (validMetadataKeys.has(k)) metadataFilters[k] = v as MetadataFilterValue;
    }
    const relationFilters = Object.fromEntries(
      Object.entries(existing?.relationFilters || {}).filter(
        ([key, value]) =>
          relationFilterFields.some((field) => field.key === key) && typeof value === 'string'
      )
    );

    let columnVisibility = existing?.columnVisibility;
    if (!columnVisibility) {
      const legacyVisible = (existing?.visibleMetadataColumns || []).filter((k) =>
        validMetadataKeys.has(k)
      );
      const visibleMetadataKeys =
        legacyVisible.length > 0 ? legacyVisible : defaultVisibleMetadataColumns;
      columnVisibility = { ...defaultColumnVisibility };
      metadataFieldKeys.forEach((key) => {
        columnVisibility![`meta:${key}`] = visibleMetadataKeys.includes(key);
      });
    } else {
      const cleaned: Record<string, boolean> = {};
      for (const [id, value] of Object.entries(columnVisibility)) {
        if (validColumnIds.has(id)) cleaned[id] = value;
      }
      columnVisibility = { ...defaultColumnVisibility, ...cleaned };
    }

    let columnOrder = existing?.columnOrder;
    if (!columnOrder) {
      const legacyMetadataOrder = (existing?.metadataColumnOrder || [])
        .filter((k) => validMetadataKeys.has(k))
        .map((k) => `meta:${k}`);
      const metadataOrder =
        legacyMetadataOrder.length > 0 ? legacyMetadataOrder : metadataColumnIds;
      columnOrder = [...baseColumnIds, ...metadataOrder];
    } else {
      columnOrder = columnOrder.filter((id) => validColumnIds.has(id));
      columnOrder = [
        ...columnOrder,
        ...defaultColumnOrderWithoutName.filter((id) => !columnOrder!.includes(id)),
      ];
    }

    const isColumnVisibilityEqual = (a: Record<string, boolean>, b: Record<string, boolean>) => {
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const key of keys) {
        if (a[key] !== b[key]) return false;
      }
      return true;
    };

    const isOrderEqual =
      columnOrder.length === (existing?.columnOrder || []).length &&
      columnOrder.every((val, idx) => (existing?.columnOrder || [])[idx] === val);
    const filtersChanged =
      Object.keys(metadataFilters).length !== Object.keys(existing?.metadataFilters || {}).length;
    const relationFiltersChanged =
      Object.keys(relationFilters).length !== Object.keys(existing?.relationFilters || {}).length ||
      Object.entries(relationFilters).some(
        ([key, value]) => existing?.relationFilters?.[key] !== value
      );
    const visibilityChanged = !isColumnVisibilityEqual(
      columnVisibility,
      existing?.columnVisibility || {}
    );
    const orderChanged = !isOrderEqual;

    if (
      filtersChanged ||
      relationFiltersChanged ||
      visibilityChanged ||
      orderChanged ||
      !existing
    ) {
      setCategoryPreferences((prev) => ({
        ...prev,
        [categoryFilter]: {
          ...existing,
          metadataFilters,
          relationFilters,
          columnVisibility: {
            ...columnVisibility,
            actions: true,
            name: true,
          },
          columnOrder,
        },
      }));
    }
  }, [
    baseColumnIds,
    categoryFilter,
    currentCategoryPref,
    defaultColumnOrderWithoutName,
    defaultColumnVisibility,
    defaultVisibleMetadataColumns,
    isCategoryLoading,
    metadataColumnIds,
    metadataFieldKeys,
    relationFilterFields,
  ]);

  const columnOrderWithoutName = categoryFilter
    ? (currentCategoryPref?.columnOrder ?? defaultColumnOrderWithoutName)
    : defaultColumnOrderWithoutName;
  const orderedColumns = columnOrderWithoutName
    .map((id) => columnDescriptorById.get(id))
    .filter((column): column is ColumnDescriptor => Boolean(column));
  const tableColumnOrder = useMemo(
    () => ['name', ...columnOrderWithoutName, 'actions'],
    [columnOrderWithoutName]
  );
  const effectiveColumnVisibility = categoryFilter
    ? (currentCategoryPref?.columnVisibility ?? defaultColumnVisibility)
    : { ...defaultGlobalColumnVisibility, ...columnVisibility };
  const sanitizedColumnVisibility = useMemo(
    () => ({
      ...effectiveColumnVisibility,
      actions: true,
      name: true,
    }),
    [effectiveColumnVisibility]
  );

  const appliedMetadataFilters = categoryFilter ? (currentCategoryPref?.metadataFilters ?? {}) : {};
  const appliedRelationFilters = categoryFilter ? (currentCategoryPref?.relationFilters ?? {}) : {};
  const activeMetadataFilterCount = getActiveFilterCount(appliedMetadataFilters);
  const activeRelationFilterCount = Object.values(appliedRelationFilters).filter((value) =>
    Boolean(value?.trim())
  ).length;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [draftMetadataFilters, setDraftMetadataFilters] = useState<
    Record<string, MetadataFilterValue>
  >({});
  const [draftRelationFilters, setDraftRelationFilters] = useState<Record<string, string>>({});
  const [favoriteOpenKey, setFavoriteOpenKey] = useState<string | null>(null);
  const [favoriteDraftValue, setFavoriteDraftValue] = useState<MetadataFilterValue | undefined>(
    undefined
  );
  const [scrollToFilterKey, setScrollToFilterKey] = useState<string | null>(null);
  const moveLabel = t('common.move');

  const columnSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!isFilterOpen) return;
    setDraftMetadataFilters(appliedMetadataFilters);
    setDraftRelationFilters(appliedRelationFilters);
  }, [isFilterOpen]);

  useEffect(() => {
    if (!isFilterOpen) return;
    if (!scrollToFilterKey) return;

    const target = document.querySelector(
      `[data-testid="metadata-filter-row-${scrollToFilterKey}"]`
    ) as HTMLElement | null;

    requestAnimationFrame(() => {
      if (!target) {
        setScrollToFilterKey(null);
        return;
      }
      target.scrollIntoView({ block: 'center' });
      const focusable = target.querySelector(
        'input, button, [role="combobox"], [role="textbox"], textarea, select'
      ) as HTMLElement | null;
      focusable?.focus?.();
      setScrollToFilterKey(null);
    });
  }, [isFilterOpen, scrollToFilterKey]);

  const handleViewDetail = useCallback(
    (object: PortalObject) => {
      const basePath = filterForCurrentUserCustomer ? '/c/objects' : '/gmn/objects';
      // Ship assignments listed under "Alle Objekte" open the register ship's detail page.
      const systemShipId = normalizeRelationshipId(object.systemShip);
      if (systemShipId) {
        navigate(`${basePath}/ships/${systemShipId}/details`);
        return;
      }
      navigate(`${basePath}/${object.id}${buildCategorySearch(categoryFilter)}`);
    },
    [navigate, filterForCurrentUserCustomer, categoryFilter]
  );

  const [objectToDelete, setObjectToDelete] = useState<PortalObject | null>(null);
  const deleteObject = useDeleteObject();

  const handleDelete = async () => {
    if (objectToDelete) {
      try {
        await deleteObject.mutateAsync(objectToDelete.id);
        toast.success(t('objects.objectDeletedSuccessfully'));
        setObjectToDelete(null);
      } catch (_error) {
        toast.error(t('objects.errorDeletingObject'));
      }
    }
  };

  const metadataColumns: ColumnDef<PortalObject>[] =
    categoryFilter && metadataFields.length
      ? metadataFields.map((field) => ({
          id: `meta:${field.key}`,
          header: () => (
            <div className="text-left font-medium whitespace-nowrap">{field.label}</div>
          ),
          cell: ({ row }: { row: Row<PortalObject> }) => {
            const values = row.original.categoryMetadataValues as
              | Record<string, unknown>
              | null
              | undefined;
            return renderMetadataCell({
              field,
              value: values?.[field.key],
              t,
              locale,
              coordinateFormat,
              yesLabel,
              noLabel,
            });
          },
          size: 180,
          minSize: 150,
          maxSize: 260,
          enableSorting: false,
        }))
      : [];

  const columns: ColumnDef<PortalObject>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.getValue('name')}</span>
      ),
      size: 250,
      minSize: 150,
      maxSize: 350,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <SortableHeader column={column} title={t('common.description')} />,
      cell: ({ row }) => (
        <span
          className="text-muted-foreground truncate max-w-[400px]"
          title={row.getValue('description')}
        >
          {row.getValue('description') || '-'}
        </span>
      ),
      size: 400,
      minSize: 250,
      maxSize: 600,
    },
    // Category column - only for GMN users
    ...(!filterForCurrentUserCustomer
      ? [
          {
            id: 'category',
            accessorKey: 'category.name',
            header: ({ column }: { column: Column<PortalObject, unknown> }) => (
              <SortableHeader column={column} title={t('objectCategories.category')} />
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const cat = row.original.category;
              return <CategoryCell category={cat} />;
            },
            size: 150,
            minSize: 100,
            maxSize: 200,
          },
        ]
      : []),
    // Only show assigned customer column for GMN users
    ...(!filterForCurrentUserCustomer
      ? [
          {
            id: 'assignedCustomer',
            accessorKey: 'assignedCustomer.name',
            header: ({ column }: { column: Column<PortalObject, unknown> }) => (
              <SortableHeader column={column} title={t('customers.assignedCustomer')} />
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const assigned = row.original.assignedCustomer;
              return <CustomerCell assignedCustomer={assigned} />;
            },
            size: 200,
            minSize: 150,
            maxSize: 300,
          },
        ]
      : []),
    ...(showAssignedHelicopterServiceProviderColumn
      ? [
          {
            id: 'assignedHelicopterServiceProvider',
            header: () => (
              <div className="text-left font-medium whitespace-nowrap">
                {t('customers.assignedHelicopterServiceProvider')}
              </div>
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const categoryName = categoryFilter
                ? filteredCategory?.name || null
                : typeof row.original.category === 'object'
                  ? row.original.category?.name || null
                  : null;
              const showValue =
                forceHelicopterTableView ||
                isHelicopterCategoryName(categoryName) ||
                isOffshoreCategoryName(categoryName);
              if (!showValue) {
                return <span className="text-muted-foreground">-</span>;
              }

              const assignedProvider = row.original.assignedHelicopterServiceProvider;
              const assignedProviderName =
                typeof assignedProvider === 'object'
                  ? assignedProvider?.name
                  : assignedProvider
                    ? heliProviderNameById.get(String(assignedProvider))
                    : undefined;

              return (
                <span
                  className="text-muted-foreground truncate"
                  title={assignedProviderName || '-'}
                >
                  {assignedProviderName || '-'}
                </span>
              );
            },
            size: 230,
            minSize: 200,
            maxSize: 320,
            enableSorting: false,
          } satisfies ColumnDef<PortalObject>,
        ]
      : []),
    ...(showLinkedOffshoreObjectsColumn
      ? [
          {
            id: 'linkedOffshoreObjects',
            header: () => (
              <div className="text-left font-medium whitespace-nowrap">
                {t('objects.linkedOffshoreObjects')}
              </div>
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const categoryName = categoryFilter
                ? filteredCategory?.name || null
                : typeof row.original.category === 'object'
                  ? row.original.category?.name || null
                  : null;
              if (!forceHelicopterTableView && !isHelicopterCategoryName(categoryName)) {
                return <span className="text-muted-foreground">-</span>;
              }

              const linkedNames =
                linkedOffshoreNamesByHelicopterId.get(String(row.original.id)) || [];
              const { display, full } = formatRelationNames(linkedNames);

              return (
                <span className="text-muted-foreground truncate" title={full}>
                  {display}
                </span>
              );
            },
            size: 260,
            minSize: 220,
            maxSize: 420,
            enableSorting: false,
          } satisfies ColumnDef<PortalObject>,
        ]
      : []),
    ...(showLinkedHelicoptersColumn
      ? [
          {
            id: 'linkedHelicopters',
            header: () => (
              <div className="text-left font-medium whitespace-nowrap">
                {t('objects.linkedHelicopters')}
              </div>
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const categoryName = categoryFilter
                ? filteredCategory?.name || null
                : typeof row.original.category === 'object'
                  ? row.original.category?.name || null
                  : null;
              if (!isOffshoreCategoryName(categoryName)) {
                return <span className="text-muted-foreground">-</span>;
              }

              const linkedIds = normalizeRelationshipIds(row.original.linkedHelicopters);
              const linkedNames = linkedIds.map((id) => linkedHelicopterNameById.get(id) || id);
              const { display, full } = formatRelationNames(linkedNames);

              return (
                <span className="text-muted-foreground truncate" title={full}>
                  {display}
                </span>
              );
            },
            size: 260,
            minSize: 220,
            maxSize: 420,
            enableSorting: false,
          } satisfies ColumnDef<PortalObject>,
        ]
      : []),
    ...(showLinkedWindparkColumn
      ? [
          {
            id: 'linkedWindpark',
            header: () => (
              <div className="text-left font-medium whitespace-nowrap">
                {t('objects.linkedWindpark')}
              </div>
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const categoryName = categoryFilter
                ? filteredCategory?.name || null
                : typeof row.original.category === 'object'
                  ? row.original.category?.name || null
                  : null;
              if (!isShipCategoryName(categoryName)) {
                return <span className="text-muted-foreground">-</span>;
              }

              const linkedId = normalizeRelationshipId(row.original.linkedWindpark);
              const linkedName = linkedId ? linkedWindparkNameById.get(linkedId) || linkedId : '-';

              return (
                <span className="text-muted-foreground truncate" title={linkedName}>
                  {linkedName}
                </span>
              );
            },
            size: 240,
            minSize: 200,
            maxSize: 360,
            enableSorting: false,
          } satisfies ColumnDef<PortalObject>,
        ]
      : []),
    ...(showLinkedShipsColumn
      ? [
          {
            id: 'linkedShips',
            header: () => (
              <div className="text-left font-medium whitespace-nowrap">
                {t('objects.linkedShips')}
              </div>
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const categoryName = categoryFilter
                ? filteredCategory?.name || null
                : typeof row.original.category === 'object'
                  ? row.original.category?.name || null
                  : null;
              if (!isWindparkCategoryName(categoryName)) {
                return <span className="text-muted-foreground">-</span>;
              }

              const linkedNames = linkedShipNamesByWindparkId.get(String(row.original.id)) || [];
              const { display, full } = formatRelationNames(linkedNames);

              return (
                <span className="text-muted-foreground truncate" title={full}>
                  {display}
                </span>
              );
            },
            size: 260,
            minSize: 220,
            maxSize: 420,
            enableSorting: false,
          } satisfies ColumnDef<PortalObject>,
        ]
      : []),
    ...metadataColumns,
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          object={row.original}
          onDelete={setObjectToDelete}
          onViewDetail={handleViewDetail}
          categoryFilter={categoryFilter}
          filterForCurrentUserCustomer={filterForCurrentUserCustomer}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  // Build where clause for filtering
  const whereAnd: Record<string, any>[] = [];
  if (debouncedSearchTerm) {
    const or: Record<string, any>[] = [
      { name: { like: debouncedSearchTerm } },
      { description: { like: debouncedSearchTerm } },
      // Clinic country ("Land"), so searching "Deutschland" finds all D- clinics
      { 'categoryMetadataValues.country': { like: debouncedSearchTerm } },
    ];
    if (!filterForCurrentUserCustomer) {
      or.push({ 'assignedCustomer.name': { like: debouncedSearchTerm } });
    }
    whereAnd.push({ or });
  }

  // Filter by category if specified in URL
  if (categoryFilter) {
    whereAnd.push({ category: { equals: categoryFilter } });
  } else if (shipCategoryIds.length > 0) {
    // Ships live in the ship register; only assignments active today (one per ship, since
    // assignments of a ship never overlap) are listed alongside the other objects.
    const today = getTodayDate();
    whereAnd.push({
      or: [
        { category: { not_in: shipCategoryIds } },
        {
          and: [
            { category: { in: shipCategoryIds } },
            { systemShip: { exists: true } },
            { 'activePeriods.startDate': { less_than_equal: today } },
            { 'activePeriods.endDate': { greater_than_equal: today } },
          ],
        },
      ],
    });
  }
  if (forcedWhereAnd.length > 0) {
    whereAnd.push(...forcedWhereAnd);
  }

  let forcedAssignedCustomerId: string | null = null;
  const assignedCustomerPath = isHeliProvider
    ? 'assignedHelicopterServiceProvider.id'
    : 'assignedCustomer.id';
  if (filterForCurrentUserCustomer && isCustomerDomain && currentUser?.customer) {
    const customerId =
      typeof currentUser.customer === 'object'
        ? currentUser.customer.id
        : (currentUser.customer as string);
    if (customerId) {
      forcedAssignedCustomerId = customerId;
      whereAnd.push({ [assignedCustomerPath]: { equals: customerId } });
    }
  }

  if (categoryFilter && Object.keys(appliedMetadataFilters).length > 0) {
    for (const [key, rawValue] of Object.entries(appliedMetadataFilters)) {
      if (!isFilterActive(rawValue)) continue;
      const field = metadataFieldByKey.get(key);
      if (!field) continue;
      if (!isFilterableField(field)) continue;

      const path = `categoryMetadataValues.${key}`;

      if (Array.isArray(rawValue)) {
        whereAnd.push({ [path]: { in: rawValue } });
        continue;
      }

      if (field.type === 'boolean') {
        const v = rawValue as BooleanFilterValue;
        if (v === 'true') whereAnd.push({ [path]: { equals: true } });
        if (v === 'false') whereAnd.push({ [path]: { equals: false } });
        continue;
      }

      if (
        field.type === 'number' &&
        typeof rawValue === 'object' &&
        rawValue &&
        'min' in rawValue
      ) {
        const range = rawValue as NumberRangeFilterValue;
        const min = range.min?.trim();
        const max = range.max?.trim();
        if (min && !Number.isNaN(Number(min))) {
          whereAnd.push({ [path]: { greater_than_equal: Number(min) } });
        }
        if (max && !Number.isNaN(Number(max))) {
          whereAnd.push({ [path]: { less_than_equal: Number(max) } });
        }
        continue;
      }

      if (field.type === 'date' && typeof rawValue === 'object' && rawValue && 'from' in rawValue) {
        const range = rawValue as DateRangeFilterValue;
        const from = range.from?.trim();
        const to = range.to?.trim();

        if (from) {
          const val = DateTime.fromISO(from).startOf('day').toUTC().toISO();
          whereAnd.push({ [path]: { greater_than_equal: val } });
        }
        if (to) {
          const val = DateTime.fromISO(to).endOf('day').toUTC().toISO();
          whereAnd.push({ [path]: { less_than_equal: val } });
        }
        continue;
      }

      if (typeof rawValue === 'string') {
        whereAnd.push({ [path]: { like: rawValue } });
      }
    }
  }

  const shouldUseOverviewEndpoint =
    Boolean(categoryFilter) && (activeMetadataFilterCount > 0 || activeRelationFilterCount > 0);

  const legacyObjectsQuery = useObjects({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where: whereAnd.length > 0 ? { and: whereAnd } : undefined,
    sort: getSortParam<PortalObject>(sorting),
    disabled: shouldUseOverviewEndpoint || (!categoryFilter && isShipCategoryOptionsLoading),
  });

  const overviewQuery = useObjectsOverview({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    category: categoryFilter || '',
    sort: getSortParam<PortalObject>(sorting),
    searchTerm: debouncedSearchTerm,
    assignedCustomerId: forcedAssignedCustomerId,
    metadataFilters: appliedMetadataFilters,
    relationFilters: appliedRelationFilters,
    disabled: !shouldUseOverviewEndpoint || !categoryFilter,
  });

  const objectsData = shouldUseOverviewEndpoint ? overviewQuery.data : legacyObjectsQuery.data;
  const isLoading = shouldUseOverviewEndpoint
    ? overviewQuery.isLoading
    : legacyObjectsQuery.isLoading;
  // True while the query shows stale placeholder data from a previous set of
  // params (e.g. right after clearing a search, before the refetch resolves).
  const isPlaceholderData = shouldUseOverviewEndpoint
    ? overviewQuery.isPlaceholderData
    : legacyObjectsQuery.isPlaceholderData;

  const objects = objectsData?.docs || [];
  const totalPages = objectsData?.totalPages || 0;
  const totalDocs = objectsData?.totalDocs || 0;

  useEffect(() => {
    if (isLoading || totalPages === 0 || pagination.pageIndex < totalPages) {
      return;
    }

    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(0, totalPages - 1),
    }));
  }, [isLoading, pagination.pageIndex, totalPages]);

  const objectIdsOnPage = useMemo(
    () => objects.map((object) => String(object.id)).filter((id) => id.trim() !== ''),
    [objects]
  );

  const pageObjectIdSet = useMemo(() => new Set(objectIdsOnPage), [objectIdsOnPage]);

  const shouldResolveHeliProviderNames =
    isHelicopterCategoryFilter || isOffshoreCategoryFilter || forceHelicopterTableView;
  const heliProviderIdsOnPage = useMemo(() => {
    if (!shouldResolveHeliProviderNames) return [];
    const providerIds = new Set<string>();
    for (const object of objects) {
      const providerId = normalizeRelationshipId(object.assignedHelicopterServiceProvider);
      if (providerId) {
        providerIds.add(providerId);
      }
    }
    return [...providerIds];
  }, [objects, shouldResolveHeliProviderNames]);

  const { data: heliProvidersData } = useCustomers({
    where: heliProviderIdsOnPage.length > 0 ? { id: { in: heliProviderIdsOnPage } } : undefined,
    limit: heliProviderIdsOnPage.length > 0 ? heliProviderIdsOnPage.length : 1,
    disabled: heliProviderIdsOnPage.length === 0,
  });

  const heliProviderNameById = useMemo(() => {
    const byId = new Map<string, string>();
    for (const provider of heliProvidersData?.docs || []) {
      byId.set(String(provider.id), provider.name);
    }
    return byId;
  }, [heliProvidersData?.docs]);

  // Offshore rows store their helicopters directly; resolve the ids on the page to names.
  const shouldResolveLinkedHelicopterNames = isOffshoreCategoryFilter;
  const linkedHelicopterIdsOnPage = useMemo(() => {
    if (!shouldResolveLinkedHelicopterNames) return [];
    const ids = new Set<string>();
    for (const object of objects) {
      for (const id of normalizeRelationshipIds(object.linkedHelicopters)) {
        ids.add(id);
      }
    }
    return [...ids];
  }, [objects, shouldResolveLinkedHelicopterNames]);

  const { data: linkedHelicopterObjectsData } = useObjects({
    where:
      linkedHelicopterIdsOnPage.length > 0 ? { id: { in: linkedHelicopterIdsOnPage } } : undefined,
    limit: linkedHelicopterIdsOnPage.length > 0 ? linkedHelicopterIdsOnPage.length : 1,
    disabled: linkedHelicopterIdsOnPage.length === 0,
  });

  const linkedHelicopterNameById = useMemo(() => {
    const byId = new Map<string, string>();
    for (const linkedObject of linkedHelicopterObjectsData?.docs || []) {
      byId.set(String(linkedObject.id), linkedObject.name);
    }
    return byId;
  }, [linkedHelicopterObjectsData?.docs]);

  const linkedWindparkIdsOnPage = useMemo(() => {
    const linkedWindparkIds = new Set<string>();
    for (const object of objects) {
      const linkedId = normalizeRelationshipId(object.linkedWindpark);
      if (linkedId) {
        linkedWindparkIds.add(linkedId);
      }
    }
    return [...linkedWindparkIds];
  }, [objects]);

  const { data: linkedWindparkObjectsData } = useObjects({
    where: linkedWindparkIdsOnPage.length > 0 ? { id: { in: linkedWindparkIdsOnPage } } : undefined,
    limit: linkedWindparkIdsOnPage.length > 0 ? linkedWindparkIdsOnPage.length : 1,
    disabled: linkedWindparkIdsOnPage.length === 0,
  });

  const linkedWindparkNameById = useMemo(() => {
    const byId = new Map<string, string>();
    for (const linkedWindpark of linkedWindparkObjectsData?.docs || []) {
      byId.set(String(linkedWindpark.id), linkedWindpark.name);
    }
    return byId;
  }, [linkedWindparkObjectsData?.docs]);

  const linkedShipsWhere = useMemo<Record<string, unknown> | undefined>(() => {
    if (!isWindparkCategoryName(filteredCategory?.name) || objectIdsOnPage.length === 0) {
      return undefined;
    }

    return {
      or: objectIdsOnPage.map((id) => ({
        linkedWindpark: {
          equals: id,
        },
      })),
    };
  }, [filteredCategory?.name, objectIdsOnPage]);

  const { data: linkedShipsData } = useObjects({
    where: linkedShipsWhere as any,
    limit: 1000,
    disabled: !linkedShipsWhere,
  });

  const linkedShipNamesByWindparkId = useMemo(() => {
    const mapByWindparkId = new Map<string, Set<string>>();

    for (const ship of linkedShipsData?.docs || []) {
      if (!isObjectActiveToday(ship)) continue;

      const shipName = ship.name || String(ship.id);
      const linkedWindparkId = normalizeRelationshipId(ship.linkedWindpark);
      if (!linkedWindparkId || !pageObjectIdSet.has(linkedWindparkId)) continue;

      const existing = mapByWindparkId.get(linkedWindparkId) || new Set<string>();
      existing.add(shipName);
      mapByWindparkId.set(linkedWindparkId, existing);
    }

    return new Map<string, string[]>(
      [...mapByWindparkId.entries()].map(([id, names]) => [
        id,
        [...names].sort((a, b) => a.localeCompare(b)),
      ])
    );
  }, [linkedShipsData?.docs, pageObjectIdSet]);

  const { data: offshoreCategoryOptions } = useObjectCategories({
    where: {
      or: [{ name: { equals: 'Windpark' } }, { name: { equals: 'Plattform' } }],
    },
    limit: 20,
  });

  const offshoreCategoryIds = useMemo(
    () => (offshoreCategoryOptions?.docs || []).map((category) => String(category.id)),
    [offshoreCategoryOptions?.docs]
  );

  // Helicopter rows: find the offshore objects that link to them (reverse lookup).
  const shouldResolveLinkedOffshoreNames = isHelicopterCategoryFilter || forceHelicopterTableView;
  const linkedOffshoreReverseWhere = useMemo<Record<string, unknown> | undefined>(() => {
    if (!shouldResolveLinkedOffshoreNames) return undefined;
    if (offshoreCategoryIds.length === 0 || objectIdsOnPage.length === 0) return undefined;
    return {
      and: [
        { category: { in: offshoreCategoryIds } },
        { or: objectIdsOnPage.map((id) => ({ linkedHelicopters: { contains: id } })) },
      ],
    } as Record<string, unknown>;
  }, [offshoreCategoryIds, objectIdsOnPage, shouldResolveLinkedOffshoreNames]);

  const { data: linkedOffshoreReverseData } = useObjects({
    where: linkedOffshoreReverseWhere as any,
    limit: 1000,
    disabled: !linkedOffshoreReverseWhere,
  });

  const linkedOffshoreNamesByHelicopterId = useMemo(() => {
    const mapByHelicopterId = new Map<string, Set<string>>();

    for (const offshore of linkedOffshoreReverseData?.docs || []) {
      const offshoreName = offshore.name || String(offshore.id);
      const linkedIds = normalizeRelationshipIds(offshore.linkedHelicopters);
      for (const helicopterId of linkedIds) {
        if (!pageObjectIdSet.has(helicopterId)) continue;
        const existing = mapByHelicopterId.get(helicopterId) || new Set<string>();
        existing.add(offshoreName);
        mapByHelicopterId.set(helicopterId, existing);
      }
    }

    return new Map<string, string[]>(
      [...mapByHelicopterId.entries()].map(([id, names]) => [
        id,
        [...names].sort((a, b) => a.localeCompare(b)),
      ])
    );
  }, [linkedOffshoreReverseData?.docs, pageObjectIdSet]);

  const table = useReactTable({
    data: objects,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility: sanitizedColumnVisibility,
      columnOrder: tableColumnOrder,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: (updater) => {
      if (categoryFilter) {
        const nextVisibility =
          typeof updater === 'function' ? updater(sanitizedColumnVisibility) : updater;
        setCategoryPreferences((prev) => {
          const existing = prev[categoryFilter] ?? {
            metadataFilters: {},
            columnVisibility: defaultColumnVisibility,
            columnOrder: defaultColumnOrderWithoutName,
          };
          return {
            ...prev,
            [categoryFilter]: {
              ...existing,
              columnVisibility: {
                ...defaultColumnVisibility,
                ...existing.columnVisibility,
                ...nextVisibility,
                actions: true,
                name: true,
              },
            },
          };
        });
      } else {
        const nextVisibility =
          typeof updater === 'function' ? updater(sanitizedColumnVisibility) : updater;
        setColumnVisibility({
          ...nextVisibility,
          actions: true,
          name: true,
        });
      }
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  const hasActiveFilters =
    Boolean(searchTerm) || activeMetadataFilterCount > 0 || activeRelationFilterCount > 0;

  // Mirror `hasActiveFilters` but use the debounced search term, which is what
  // the currently displayed results actually reflect. Using the immediate term
  // would briefly flash the empty state while clearing a search: the term is
  // already empty but the query (and its results) still lag behind by the
  // debounce interval.
  const hasActiveFiltersForResults =
    Boolean(debouncedSearchTerm) || activeMetadataFilterCount > 0 || activeRelationFilterCount > 0;

  // Only treat the account as "no objects assigned" when there are no active
  // filters. Otherwise a search/filter that returns zero results would hide the
  // filter UI entirely and trap the user in the empty state (the search term is
  // persisted, so it survives a reload). Also require fresh (non-placeholder)
  // data so we don't flash the empty state while a refetch is in flight.
  const showCustomerEmptyState =
    filterForCurrentUserCustomer &&
    !isLoading &&
    !isPlaceholderData &&
    !hasActiveFiltersForResults &&
    (objectsData?.totalDocs ?? 0) === 0;
  const showObjectsTable = !showCustomerEmptyState;

  // Determine title and breadcrumb
  const pageTitle = filterForCurrentUserCustomer
    ? t('objects.myObjects')
    : filteredCategory
      ? `${t('objects.objects')}: ${filteredCategory.name}`
      : t('objects.manageObjects');

  const breadcrumbTitle = filterForCurrentUserCustomer
    ? t('objects.myObjects')
    : filteredCategory
      ? filteredCategory.name
      : t('navigation.items.allObjects');

  const pageIcon = filteredCategory ? getObjectCategoryIcon(filteredCategory.icon) : Box;
  const pageIconKey = filteredCategory
    ? normalizeObjectCategoryIconKey(filteredCategory.icon)
    : null;

  const canUseMetadataFilters =
    Boolean(categoryFilter) &&
    !isCategoryLoading &&
    (metadataFields.length > 0 || relationFilterFields.length > 0);
  const canUseColumnPreferences = Boolean(categoryFilter) && !isCategoryLoading;

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!categoryFilter) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = columnOrderWithoutName.indexOf(active.id as string);
      const newIndex = columnOrderWithoutName.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextOrder = arrayMove(columnOrderWithoutName, oldIndex, newIndex);
      setCategoryPreferences((prev) => {
        const existing = prev[categoryFilter] ?? {
          metadataFilters: {},
          columnVisibility: defaultColumnVisibility,
          columnOrder: defaultColumnOrderWithoutName,
        };
        return {
          ...prev,
          [categoryFilter]: {
            ...existing,
            columnOrder: nextOrder,
          },
        };
      });
    },
    [
      categoryFilter,
      columnOrderWithoutName,
      defaultColumnOrderWithoutName,
      defaultColumnVisibility,
      setCategoryPreferences,
    ]
  );

  const handleApplyMetadataFilters = () => {
    if (!categoryFilter) return;
    setCategoryPreferences((prev) => ({
      ...prev,
      [categoryFilter]: {
        ...(prev[categoryFilter] ?? {
          metadataFilters: {},
          relationFilters: {},
          columnVisibility: defaultColumnVisibility,
          columnOrder: defaultColumnOrderWithoutName,
        }),
        metadataFilters: draftMetadataFilters,
        relationFilters: Object.fromEntries(
          Object.entries(draftRelationFilters).filter(([, value]) => value.trim() !== '')
        ),
      },
    }));
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setIsFilterOpen(false);
  };

  const handleClearMetadataFilters = () => {
    if (!categoryFilter) return;
    setCategoryPreferences((prev) => ({
      ...prev,
      [categoryFilter]: {
        ...(prev[categoryFilter] ?? {
          metadataFilters: {},
          relationFilters: {},
          columnVisibility: defaultColumnVisibility,
          columnOrder: defaultColumnOrderWithoutName,
        }),
        metadataFilters: {},
        relationFilters: {},
      },
    }));
    setDraftMetadataFilters({});
    setDraftRelationFilters({});
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleRemoveMetadataFilter = (key: string) => {
    if (!categoryFilter) return;
    setCategoryPreferences((prev) => {
      const existing = prev[categoryFilter];
      if (!existing) return prev;
      const next = { ...existing.metadataFilters };
      delete next[key];
      return {
        ...prev,
        [categoryFilter]: { ...existing, metadataFilters: next },
      };
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleRemoveRelationFilter = (key: string) => {
    if (!categoryFilter) return;
    setCategoryPreferences((prev) => {
      const existing = prev[categoryFilter];
      if (!existing) return prev;
      const next = { ...(existing.relationFilters || {}) };
      delete next[key];
      return {
        ...prev,
        [categoryFilter]: { ...existing, relationFilters: next },
      };
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const applySingleMetadataFilter = (key: string, value: MetadataFilterValue | undefined) => {
    if (!categoryFilter) return;
    setCategoryPreferences((prev) => {
      const existing = prev[categoryFilter] ?? {
        metadataFilters: {},
        relationFilters: {},
        columnVisibility: defaultColumnVisibility,
        columnOrder: defaultColumnOrderWithoutName,
      };

      const next = { ...existing.metadataFilters };
      if (value !== undefined && isFilterActive(value)) {
        next[key] = value;
      } else {
        delete next[key];
      }

      return {
        ...prev,
        [categoryFilter]: { ...existing, metadataFilters: next },
      };
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const MAX_INLINE_FAVORITES = 3;
  const inlineFavoriteFields = favoriteFilterFields.slice(0, MAX_INLINE_FAVORITES);
  const overflowFavoriteFields = favoriteFilterFields.slice(MAX_INLINE_FAVORITES);

  const filterControls = (
    <>
      {canUseMetadataFilters && favoriteFilterFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {inlineFavoriteFields.map((field) => {
            const isActive = isFilterActive(appliedMetadataFilters[field.key]);
            const isOpen = favoriteOpenKey === field.key;
            const draftValue = isOpen ? favoriteDraftValue : appliedMetadataFilters[field.key];

            return (
              <Popover
                key={field.key}
                open={isOpen}
                onOpenChange={(open) => {
                  if (open) {
                    setFavoriteDraftValue(appliedMetadataFilters[field.key]);
                    setFavoriteOpenKey(field.key);
                  } else if (favoriteOpenKey === field.key) {
                    setFavoriteOpenKey(null);
                    setFavoriteDraftValue(undefined);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`objects-favorite-filter-${field.key}`}
                    title={field.label}
                    className={cn(
                      'h-9 px-3 text-sm max-w-[180px]',
                      (isActive || isOpen) && 'bg-muted border-border text-foreground'
                    )}
                  >
                    <span className="truncate">{field.label}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[340px] p-0"
                  sideOffset={8}
                  data-testid={`objects-favorite-filter-popover-${field.key}`}
                >
                  <div className="flex flex-col max-h-[70vh]">
                    <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold text-sm truncate" title={field.label}>
                        {field.label}
                      </h3>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setFavoriteOpenKey(null);
                          setFavoriteDraftValue(undefined);
                        }}
                        aria-label={t('common.close')}
                        className="h-8 w-8"
                      >
                        <X size={16} />
                      </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-4">
                      <MetadataFilterControl
                        field={field}
                        value={draftValue}
                        onChange={(next) => setFavoriteDraftValue(next)}
                        t={t}
                        locale={locale}
                        testIdPrefix={`metadata-favorite-${field.key}`}
                      />
                    </div>

                    <div className="p-3 border-t border-border bg-muted/40 flex justify-between items-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid={`objects-favorite-filter-${field.key}-clear`}
                        onClick={() => {
                          applySingleMetadataFilter(field.key, undefined);
                          setFavoriteOpenKey(null);
                          setFavoriteDraftValue(undefined);
                        }}
                      >
                        {t('common.reset')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        data-testid={`objects-favorite-filter-${field.key}-apply`}
                        onClick={() => {
                          applySingleMetadataFilter(field.key, draftValue);
                          setFavoriteOpenKey(null);
                          setFavoriteDraftValue(undefined);
                        }}
                      >
                        {t('common.apply')}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}

          {overflowFavoriteFields.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 px-2"
                  data-testid="objects-favorite-filters-more"
                >
                  +{overflowFavoriteFields.length}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>{t('common.filter')}</DropdownMenuLabel>
                {overflowFavoriteFields.map((field) => (
                  <DropdownMenuItem
                    key={field.key}
                    onClick={() => {
                      setIsFilterOpen(true);
                      setScrollToFilterKey(field.key);
                    }}
                    data-testid={`objects-favorite-filters-more-${field.key}`}
                  >
                    {field.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={!canUseMetadataFilters}
            data-testid="objects-filters-open"
            className={cn(
              'h-9 flex items-center gap-2',
              (activeMetadataFilterCount > 0 || activeRelationFilterCount > 0 || isFilterOpen) &&
                'bg-muted border-border text-foreground'
            )}
          >
            <Filter size={16} />
            {t('common.filter')}
            {activeMetadataFilterCount + activeRelationFilterCount > 0 && (
              <span className="bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded-full min-w-5 text-center">
                {activeMetadataFilterCount + activeRelationFilterCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-[340px] p-0" sideOffset={8}>
          <div className="flex flex-col max-h-[50vh]">
            <div className="px-3 py-2 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-sm">{t('common.filter')}</h3>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setIsFilterOpen(false)}
                aria-label={t('common.close')}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {relationFilterFields.length > 0 && (
                <div className="space-y-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('objects.relations')}
                  </div>

                  {relationFilterFields.map((field) => (
                    <div
                      key={field.key}
                      className="space-y-1.5"
                      data-testid={`relation-filter-row-${field.key}`}
                    >
                      <Label className="text-sm font-medium text-foreground">{field.label}</Label>
                      <Input
                        value={draftRelationFilters[field.key] || ''}
                        onChange={(event) =>
                          setDraftRelationFilters((prev) => ({
                            ...prev,
                            [field.key]: event.target.value,
                          }))
                        }
                        placeholder={t('common.search')}
                        data-testid={`relation-filter-${field.key}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {filteredCategory?.name
                    ? (t as (key: string, options?: Record<string, unknown>) => string)(
                        'objects.categoryAttributes',
                        { category: filteredCategory.name }
                      )
                    : t('objects.attributes')}
                </div>

                {filterableMetadataFields.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {t('objects.noFilterableMetadata')}
                  </div>
                ) : (
                  filterableMetadataFields.map((field) => {
                    const label = field.label;
                    const value = draftMetadataFilters[field.key];

                    return (
                      <div
                        key={field.key}
                        className="space-y-1.5"
                        data-testid={`metadata-filter-row-${field.key}`}
                      >
                        <Label className="text-sm font-medium text-foreground">{label}</Label>
                        <MetadataFilterControl
                          field={field}
                          value={value}
                          onChange={(next) =>
                            setDraftMetadataFilters((prev) => ({
                              ...prev,
                              [field.key]: next,
                            }))
                          }
                          t={t}
                          locale={locale}
                          testIdPrefix={`metadata-filter-${field.key}`}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-3 border-t border-border bg-muted/40 flex justify-between items-center">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearMetadataFilters}
                data-testid="objects-filters-clear"
                size="sm"
              >
                {t('common.clearAll')}
              </Button>
              <Button
                type="button"
                onClick={handleApplyMetadataFilters}
                data-testid="objects-filters-apply"
                size="sm"
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );

  const layoutActions = (
    <div className="flex items-center gap-2">
      <Popover open={isColumnsOpen} onOpenChange={setIsColumnsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={!canUseColumnPreferences}
            data-testid="objects-columns-open"
            className="h-9 flex items-center gap-2"
          >
            <SlidersHorizontal size={16} />
            {t('common.columns')}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-1" sideOffset={8}>
          <div className="p-1 max-h-96 overflow-y-auto">
            <div className="px-2 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('common.columns')}
            </div>

            {columnDescriptors.length === 0 ? (
              <div className="px-2 py-2 text-sm text-muted-foreground">{t('common.noResults')}</div>
            ) : (
              <DndContext
                sensors={columnSensors}
                collisionDetection={pointerFirstCollisionDetection}
                onDragEnd={handleColumnDragEnd}
              >
                <SortableContext
                  items={orderedColumns.map((column) => column.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1">
                    {orderedColumns.map((column) => {
                      const isChecked =
                        effectiveColumnVisibility[column.id] ??
                        defaultColumnVisibility[column.id] ??
                        true;
                      return (
                        <SortableMetadataColumnRow
                          key={column.id}
                          column={column}
                          checked={isChecked}
                          moveLabel={moveLabel}
                          onCheckedChange={(checked) => {
                            if (!categoryFilter) return;
                            setCategoryPreferences((prev) => {
                              const existing = prev[categoryFilter] ?? {
                                metadataFilters: {},
                                relationFilters: {},
                                columnVisibility: defaultColumnVisibility,
                                columnOrder: defaultColumnOrderWithoutName,
                              };
                              return {
                                ...prev,
                                [categoryFilter]: {
                                  ...existing,
                                  columnVisibility: {
                                    ...defaultColumnVisibility,
                                    ...existing.columnVisibility,
                                    [column.id]: checked,
                                  },
                                },
                              };
                            });
                          }}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <div className="border-t border-border mt-2 pt-2 px-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                data-testid="objects-columns-reset"
                onClick={() => {
                  if (!categoryFilter) return;
                  setCategoryPreferences((prev) => {
                    const existing = prev[categoryFilter] ?? {
                      metadataFilters: {},
                      relationFilters: {},
                      columnVisibility: defaultColumnVisibility,
                      columnOrder: defaultColumnOrderWithoutName,
                    };
                    return {
                      ...prev,
                      [categoryFilter]: {
                        ...existing,
                        columnVisibility: defaultColumnVisibility,
                        columnOrder: defaultColumnOrderWithoutName,
                      },
                    };
                  });
                }}
              >
                {t('common.reset')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setFavoriteOpenKey(null);
    setFavoriteDraftValue(undefined);
    setIsFilterOpen(false);
    handleClearMetadataFilters();
  };

  const activeFilterTags = (() => {
    const tags: Array<{
      id: string;
      label: ReactNode;
      onRemove: () => void;
      testId?: string;
    }> = [];

    if (searchTerm) {
      tags.push({
        id: 'search',
        label: (
          <>
            <span className="font-medium text-foreground">{t('common.searchLabel')}:</span>
            <span className="truncate max-w-[240px]" title={searchTerm}>
              {searchTerm}
            </span>
          </>
        ),
        onRemove: () => handleSearchChange(''),
      });
    }

    if (canUseMetadataFilters && activeMetadataFilterCount > 0) {
      for (const [key, value] of Object.entries(appliedMetadataFilters)) {
        if (!isFilterActive(value)) continue;
        const field = metadataFieldByKey.get(key);
        if (!field) continue;
        const display = formatFilterValueForChip(
          value,
          { yes: yesLabel, no: noLabel },
          field,
          locale
        );
        if (!display) continue;

        tags.push({
          id: `metadata-${key}`,
          testId: `metadata-filter-chip-${key}`,
          label: (
            <>
              <span className="font-medium text-foreground">{field.label}:</span>
              <span className="truncate max-w-[240px]" title={display}>
                {display}
              </span>
            </>
          ),
          onRemove: () => handleRemoveMetadataFilter(key),
        });
      }
    }

    if (relationFilterFields.length > 0) {
      for (const field of relationFilterFields) {
        const value = appliedRelationFilters[field.key]?.trim();
        if (!value) continue;

        tags.push({
          id: `relation-${field.key}`,
          testId: `relation-filter-chip-${field.key}`,
          label: (
            <>
              <span className="font-medium text-foreground">{field.label}:</span>
              <span className="truncate max-w-[240px]" title={value}>
                {value}
              </span>
            </>
          ),
          onRemove: () => handleRemoveRelationFilter(field.key),
        });
      }
    }

    return tags;
  })();

  if (categoryFilter && !isCategoryLoading && showLinkedWindparkColumn) {
    return (
      <SystemShipsOverview
        categoryFilter={categoryFilter}
        filterForCurrentUserCustomer={filterForCurrentUserCustomer}
      />
    );
  }

  const content = (
    <TooltipProvider>
      {!hidePageTitle && (
        <PageTitle
          icon={pageIcon}
          iconTestId={filteredCategory ? 'objects-category-icon' : undefined}
          iconKey={pageIconKey ?? undefined}
          breadcrumb={
            <>
              <SimpleBreadcrumbItem>{t('objects.objects')}</SimpleBreadcrumbItem>
              <SimpleBreadcrumbItem isLast>{breadcrumbTitle}</SimpleBreadcrumbItem>
            </>
          }
          title={pageTitle}
          primaryAction={
            !filterForCurrentUserCustomer
              ? {
                  label: t('objects.createObject'),
                  onClick: () => navigate(`/gmn/objects/new${buildCategorySearch(categoryFilter)}`),
                  icon: <Plus size={16} />,
                  disabled: !canCreateObjectsForCategory,
                }
              : undefined
          }
        />
      )}

      {showObjectsTable ? (
        <>
          <TableCard className="mb-4">
            <TableCardHeader>
              <TableSearchBar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={t('objects.searchPlaceholder')}
                actions={layoutActions}
              />
            </TableCardHeader>
          </TableCard>

          <div className="mb-4 space-y-3">
            <TableFiltersBar
              clearAll={
                hasActiveFilters ? (
                  <TableClearAllButton
                    onClick={handleClearAllFilters}
                    testId="objects-filters-clear-all"
                  />
                ) : undefined
              }
            >
              {filterControls}
            </TableFiltersBar>

            <TableActiveFilterTags tags={activeFilterTags} />
          </div>

          <TableCard className="mb-6">
            <TableCardContent>
              <div className="overflow-x-auto">
                <Table className="min-w-max">
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableHeaderRow key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => {
                          const isStickyLeft = header.column.id === 'name';
                          const isStickyRight = header.column.id === 'actions';

                          return (
                            <TableHead
                              key={header.id}
                              style={{ width: `${header.getSize()}px` }}
                              className={cn(
                                index === 0 ? 'pl-6' : '',
                                index === headerGroup.headers.length - 1 ? 'pr-6 text-right' : '',
                                isStickyLeft &&
                                  'sticky left-0 z-30 bg-muted shadow-[inset_-1px_0_0_hsl(var(--border)),4px_0_12px_-4px_rgba(0,0,0,0.08)]',
                                isStickyRight &&
                                  'sticky right-0 z-30 bg-transparent shadow-[inset_1px_0_0_hsl(var(--border))]'
                              )}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                          );
                        })}
                      </TableHeaderRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          className="cursor-pointer hover:bg-muted"
                          onClick={() => handleViewDetail(row.original)}
                        >
                          {row.getVisibleCells().map((cell, index) => {
                            const isStickyLeft = cell.column.id === 'name';
                            const isStickyRight = cell.column.id === 'actions';

                            return (
                              <TableCell
                                key={cell.id}
                                style={{ width: `${cell.column.getSize()}px` }}
                                className={cn(
                                  index === 0 ? 'pl-6' : '',
                                  index === row.getVisibleCells().length - 1 ? 'pr-6' : '',
                                  isStickyLeft &&
                                    'sticky left-0 z-20 bg-card group-hover:bg-card shadow-[inset_-1px_0_0_hsl(var(--border)),4px_0_12px_-4px_rgba(0,0,0,0.08)]',
                                  isStickyRight &&
                                    'sticky right-0 z-20 bg-transparent group-hover:bg-muted transition-colors shadow-[inset_1px_0_0_hsl(var(--border))]'
                                )}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    ) : !isLoading ? (
                      <EmptyTableRow colSpan={columns.length} />
                    ) : (
                      <TableLoadingCell colSpan={columns.length} />
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableCardContent>

            <TableCardFooter>
              <TablePagination table={table} />
            </TableCardFooter>
          </TableCard>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <PackageSearch className="size-5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('objects.myObjectsEmptyTitle')}</p>
              <p className="text-sm text-muted-foreground">
                {t('objects.myObjectsEmptyDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      {!filterForCurrentUserCustomer && (
        <AlertDialog
          open={!!objectToDelete}
          onOpenChange={(open) => !open && setObjectToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('objects.deleteObject')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('objects.deleteObjectConfirmation', {
                  name: objectToDelete?.name || t('objects.object'),
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleteObject.isPending}>
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </TooltipProvider>
  );

  if (embedded) {
    return content;
  }

  return <PageContainer>{content}</PageContainer>;
}
